import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavbar from '../../components/ui/TopNavbar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import WorkflowStageCard from './components/WorkflowStageCard';
import StageDetailsModal from './components/StageDetailsModal';
import WorkflowTimeline from './components/WorkflowTimeline';

const WorkflowManagement = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStage, setSelectedStage] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'timeline'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'active', 'completed', 'delayed'

  const workflowStages = [
    {
      id: 1,
      name: 'Counterparty Onboarding',
      timing: 'Pre-contract (2-4 weeks before trade)',
      objectives: 'Complete KYC/AML verification, establish credit terms, set up banking relationships',
      contacts: [
        { name: 'Sarah Mitchell', role: 'Compliance Officer', email: 'sarah.mitchell@rva.com', phone: '+91 98765 43210' },
        { name: 'James Chen', role: 'Credit Manager', email: 'james.chen@rva.com', phone: '+91 98765 43211' }
      ],
      backupContacts: [
        { name: 'Emily Rodriguez', role: 'Senior Compliance', email: 'emily.rodriguez@rva.com', phone: '+91 98765 43212' }
      ],
      requiredOutputs: [
        'KYC documentation approved',
        'Credit limit established',
        'Banking coordinates verified',
        'Trade authorization signed'
      ],
      status: 'completed',
      progress: 100,
      completedDate: '2025-11-15',
      icon: 'Users',
      color: 'primary'
    },
    {
      id: 2,
      name: 'Contract Finalization',
      timing: 'T-30 to T-15 days before shipment',
      objectives: 'Negotiate and execute coal supply agreement, define specifications, pricing, delivery terms',
      contacts: [
        { name: 'Michael Kumar', role: 'Trading Manager', email: 'michael.kumar@rva.com', phone: '+91 98765 43213' },
        { name: 'Lisa Thompson', role: 'Legal Counsel', email: 'lisa.thompson@rva.com', phone: '+91 98765 43214' }
      ],
      backupContacts: [
        { name: 'David Park', role: 'Senior Trader', email: 'david.park@rva.com', phone: '+91 98765 43215' }
      ],
      requiredOutputs: [
        'Signed coal supply contract',
        'Quality specifications confirmed',
        'Payment terms agreed',
        'Force majeure clauses defined'
      ],
      status: 'active',
      progress: 75,
      startDate: '2025-12-01',
      expectedCompletion: '2025-12-20',
      icon: 'FileText',
      color: 'success'
    },
    {
      id: 3,
      name: 'Rail/Inland Logistics Alignment',
      timing: 'T-20 to T-10 days',
      objectives: 'Coordinate rail/truck transport from mine to port, ensure capacity booking, track movement',
      contacts: [
        { name: 'Rajesh Sharma', role: 'Logistics Coordinator', email: 'rajesh.sharma@rva.com', phone: '+91 98765 43216' },
        { name: 'Priya Patel', role: 'Transport Manager', email: 'priya.patel@rva.com', phone: '+91 98765 43217' }
      ],
      backupContacts: [
        { name: 'Amit Singh', role: 'Logistics Lead', email: 'amit.singh@rva.com', phone: '+91 98765 43218' }
      ],
      requiredOutputs: [
        'Rail wagon allocation confirmed',
        'Transport schedule published',
        'Truck fleet arrangements made',
        'Route clearances obtained'
      ],
      status: 'active',
      progress: 60,
      startDate: '2025-12-05',
      expectedCompletion: '2025-12-18',
      icon: 'Truck',
      color: 'accent'
    },
    {
      id: 4,
      name: 'Terminal/Port Pathway Selection',
      timing: 'T-15 to T-7 days',
      objectives: 'Select optimal loading terminal, book berth slot, arrange stevedoring services',
      contacts: [
        { name: 'Arun Desai', role: 'Port Operations Manager', email: 'arun.desai@rva.com', phone: '+91 98765 43219' },
        { name: 'Neha Gupta', role: 'Terminal Coordinator', email: 'neha.gupta@rva.com', phone: '+91 98765 43220' }
      ],
      backupContacts: [
        { name: 'Vikram Rao', role: 'Senior Port Manager', email: 'vikram.rao@rva.com', phone: '+91 98765 43221' }
      ],
      requiredOutputs: [
        'Berth booking confirmation',
        'Loading terminal selected',
        'Stevedoring contract signed',
        'Port fees agreed'
      ],
      status: 'pending',
      progress: 0,
      expectedStart: '2025-12-12',
      icon: 'Anchor',
      color: 'secondary'
    },
    {
      id: 5,
      name: 'Vessel Nomination & Port Call Setup',
      timing: 'T-10 to T-5 days',
      objectives: 'Nominate vessel, file port call notice, coordinate with port authorities and agents',
      contacts: [
        { name: 'Captain Robert Lee', role: 'Shipping Coordinator', email: 'robert.lee@rva.com', phone: '+91 98765 43222' },
        { name: 'Maria Santos', role: 'Port Agent Liaison', email: 'maria.santos@rva.com', phone: '+91 98765 43223' }
      ],
      backupContacts: [
        { name: 'John Wilson', role: 'Marine Operations', email: 'john.wilson@rva.com', phone: '+91 98765 43224' }
      ],
      requiredOutputs: [
        'Vessel nomination accepted',
        'Port call notice filed',
        'Agent appointment confirmed',
        'Berth window allocated'
      ],
      status: 'pending',
      progress: 0,
      expectedStart: '2025-12-17',
      icon: 'Ship',
      color: 'primary'
    },
    {
      id: 6,
      name: 'Inspection & Sampling Plan Appointment',
      timing: 'T-7 days to loading start',
      objectives: 'Appoint independent surveyor, define sampling protocol, schedule lab testing',
      contacts: [
        { name: 'Dr. Suresh Menon', role: 'Quality Manager', email: 'suresh.menon@rva.com', phone: '+91 98765 43225' },
        { name: 'Anjali Reddy', role: 'Lab Coordinator', email: 'anjali.reddy@rva.com', phone: '+91 98765 43226' }
      ],
      backupContacts: [
        { name: 'Dr. Kavita Shah', role: 'Senior Quality Lead', email: 'kavita.shah@rva.com', phone: '+91 98765 43227' }
      ],
      requiredOutputs: [
        'Surveyor appointment letter',
        'Sampling plan approved',
        'Lab booking confirmed',
        'Testing schedule published'
      ],
      status: 'pending',
      progress: 0,
      expectedStart: '2025-12-20',
      icon: 'ClipboardCheck',
      color: 'warning'
    },
    {
      id: 7,
      name: 'Quantity/Draft Survey at Loading',
      timing: 'During loading operations (3-5 days)',
      objectives: 'Verify loaded quantity, conduct draft survey, confirm vessel stability',
      contacts: [
        { name: 'Captain Thomas Anderson', role: 'Marine Surveyor', email: 'thomas.anderson@survey.com', phone: '+91 98765 43228' },
        { name: 'Ravi Kumar', role: 'Loading Supervisor', email: 'ravi.kumar@rva.com', phone: '+91 98765 43229' }
      ],
      backupContacts: [
        { name: 'Captain Sarah Johnson', role: 'Senior Surveyor', email: 'sarah.johnson@survey.com', phone: '+91 98765 43230' }
      ],
      requiredOutputs: [
        'Draft survey report',
        'Bill of Lading quantity',
        'Loading time sheet',
        'Vessel stability certificate'
      ],
      status: 'pending',
      progress: 0,
      expectedStart: '2025-12-25',
      icon: 'Scale',
      color: 'success'
    },
    {
      id: 8,
      name: 'Loading Operations Governance',
      timing: 'During loading (continuous)',
      objectives: 'Monitor loading rate, ensure safety compliance, track demurrage, manage operations',
      contacts: [
        { name: 'Prakash Joshi', role: 'Operations Supervisor', email: 'prakash.joshi@rva.com', phone: '+91 98765 43231' },
        { name: 'Sunita Iyer', role: 'Safety Officer', email: 'sunita.iyer@rva.com', phone: '+91 98765 43232' }
      ],
      backupContacts: [
        { name: 'Ramesh Nair', role: 'Senior Operations', email: 'ramesh.nair@rva.com', phone: '+91 98765 43233' }
      ],
      requiredOutputs: [
        'Hourly loading reports',
        'Safety compliance logs',
        'Demurrage tracking sheet',
        'Operations summary'
      ],
      status: 'pending',
      progress: 0,
      expectedStart: '2025-12-25',
      icon: 'Activity',
      color: 'accent'
    },
    {
      id: 9,
      name: 'Documentation & LC Presentation',
      timing: 'Within 7-14 days post-loading',
      objectives: 'Compile shipping documents, present to bank for LC payment, ensure compliance',
      contacts: [
        { name: 'Meera Krishnan', role: 'Trade Finance Manager', email: 'meera.krishnan@rva.com', phone: '+91 98765 43234' },
        { name: 'Arjun Pillai', role: 'Documentation Officer', email: 'arjun.pillai@rva.com', phone: '+91 98765 43235' }
      ],
      backupContacts: [
        { name: 'Deepak Agarwal', role: 'Senior Finance Lead', email: 'deepak.agarwal@rva.com', phone: '+91 98765 43236' }
      ],
      requiredOutputs: [
        'Complete document set',
        'LC presentation acknowledgment',
        'Bank acceptance confirmation',
        'Payment schedule received'
      ],
      status: 'pending',
      progress: 0,
      expectedStart: '2025-12-30',
      icon: 'FileCheck',
      color: 'secondary'
    },
    {
      id: 10,
      name: 'Insurance Placement',
      timing: 'Pre-loading to post-discharge',
      objectives: 'Arrange marine cargo insurance, confirm coverage, manage claims process',
      contacts: [
        { name: 'Kavita Deshmukh', role: 'Insurance Manager', email: 'kavita.deshmukh@rva.com', phone: '+91 98765 43237' },
        { name: 'Sanjay Verma', role: 'Risk Coordinator', email: 'sanjay.verma@rva.com', phone: '+91 98765 43238' }
      ],
      backupContacts: [
        { name: 'Pooja Mehta', role: 'Senior Risk Manager', email: 'pooja.mehta@rva.com', phone: '+91 98765 43239' }
      ],
      requiredOutputs: [
        'Insurance certificate issued',
        'Coverage confirmation',
        'Premium payment receipt',
        'Claims process documented'
      ],
      status: 'pending',
      progress: 0,
      expectedStart: '2025-12-22',
      icon: 'Shield',
      color: 'primary'
    },
    {
      id: 11,
      name: 'Post-Shipment Claims & Reconciliation',
      timing: 'Post-discharge (30-60 days)',
      objectives: 'Resolve quantity/quality claims, reconcile invoices, finalize payments',
      contacts: [
        { name: 'Rahul Bose', role: 'Claims Manager', email: 'rahul.bose@rva.com', phone: '+91 98765 43240' },
        { name: 'Divya Nambiar', role: 'Reconciliation Lead', email: 'divya.nambiar@rva.com', phone: '+91 98765 43241' }
      ],
      backupContacts: [
        { name: 'Kiran Jain', role: 'Senior Claims Officer', email: 'kiran.jain@rva.com', phone: '+91 98765 43242' }
      ],
      requiredOutputs: [
        'Claims settlement report',
        'Final invoice reconciliation',
        'Payment confirmation',
        'Dispute resolution logs'
      ],
      status: 'pending',
      progress: 0,
      expectedStart: '2026-01-15',
      icon: 'DollarSign',
      color: 'warning'
    },
    {
      id: 12,
      name: 'Environmental/Compliance Support',
      timing: 'Throughout entire workflow',
      objectives: 'Ensure environmental compliance, maintain regulatory documentation, audit readiness',
      contacts: [
        { name: 'Dr. Nisha Kapoor', role: 'Environmental Officer', email: 'nisha.kapoor@rva.com', phone: '+91 98765 43243' },
        { name: 'Vijay Sharma', role: 'Compliance Manager', email: 'vijay.sharma@rva.com', phone: '+91 98765 43244' }
      ],
      backupContacts: [
        { name: 'Dr. Arvind Malhotra', role: 'Environmental Lead', email: 'arvind.malhotra@rva.com', phone: '+91 98765 43245' }
      ],
      requiredOutputs: [
        'Environmental clearances',
        'Compliance certificates',
        'Audit trail documentation',
        'Regulatory reporting'
      ],
      status: 'active',
      progress: 100,
      icon: 'Leaf',
      color: 'success'
    }
  ];

  const handleStageClick = (stage) => {
    setSelectedStage(stage);
  };

  const handleCloseModal = () => {
    setSelectedStage(null);
  };

  const handleUpdateStage = (updatedStage) => {
    console.log('Update stage:', updatedStage);
    setSelectedStage(null);
  };

  const filteredStages = workflowStages?.filter(stage => {
    const matchesSearch = stage?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                          stage?.objectives?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesFilter = filterStatus === 'all' || stage?.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusCounts = () => {
    return {
      all: workflowStages?.length,
      active: workflowStages?.filter(s => s?.status === 'active')?.length,
      completed: workflowStages?.filter(s => s?.status === 'completed')?.length,
      pending: workflowStages?.filter(s => s?.status === 'pending')?.length,
      delayed: workflowStages?.filter(s => s?.status === 'delayed')?.length
    };
  };

  const statusCounts = getStatusCounts();

  return (
    <div className="min-h-screen bg-background">
      <TopNavbar />
      <div className="main-content-with-topnav">
        {/* Header Section */}
        <div className="sticky top-[65px] z-40 bg-card border-b border-border">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Workflow Management</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  12-stage coal trading operations workflow
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button 
                  variant={viewMode === 'grid' ? 'default' : 'outline'} 
                  size="sm" 
                  iconName="Grid3x3"
                  onClick={() => setViewMode('grid')}
                />
                <Button 
                  variant={viewMode === 'timeline' ? 'default' : 'outline'} 
                  size="sm" 
                  iconName="Calendar"
                  onClick={() => setViewMode('timeline')}
                />
                <Button variant="outline" size="sm" iconName="Download">
                  Export
                </Button>
                <Button variant="default" size="sm" iconName="Plus">
                  New Workflow
                </Button>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex items-center gap-4">
              <div className="flex-1 max-w-md">
                <Input
                  type="text"
                  placeholder="Search stages by name or objective..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  iconName="Search"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant={filterStatus === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('all')}
                >
                  All ({statusCounts?.all})
                </Button>
                <Button
                  variant={filterStatus === 'active' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('active')}
                >
                  Active ({statusCounts?.active})
                </Button>
                <Button
                  variant={filterStatus === 'completed' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('completed')}
                >
                  Completed ({statusCounts?.completed})
                </Button>
                <Button
                  variant={filterStatus === 'pending' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('pending')}
                >
                  Pending ({statusCounts?.pending})
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6">
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredStages?.map((stage) => (
                <WorkflowStageCard
                  key={stage?.id}
                  stage={stage}
                  onClick={() => handleStageClick(stage)}
                />
              ))}
            </div>
          ) : (
            <WorkflowTimeline 
              stages={filteredStages} 
              onStageClick={handleStageClick}
            />
          )}

          {filteredStages?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <Icon name="Search" size={48} className="text-muted-foreground mb-4" />
              <p className="text-lg text-muted-foreground">No stages found</p>
              <p className="text-sm text-muted-foreground mt-2">
                Try adjusting your search or filters
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Stage Details Modal */}
      {selectedStage && (
        <StageDetailsModal
          stage={selectedStage}
          onClose={handleCloseModal}
          onUpdate={handleUpdateStage}
        />
      )}
    </div>
  );
};

export default WorkflowManagement;